import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { inspectionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { cn, formatDate, formatDateTime, getStatusColor, getRecordTypeLabel, getInspectionTypeLabel, downloadBlob } from '../lib/utils';
import {
  Search,
  Filter,
  Plus,
  Loader2,
  ClipboardCheck,
  Download,
  QrCode,
  Eye,
  CheckCircle,
  XCircle,
  MinusCircle,
  AlertCircle,
  Calendar,
  User,
  MapPin,
  Cylinder,
  FileText,
  History,
  Printer,
  Image,
} from 'lucide-react';
import { toast } from 'sonner';

export default function InspectionsListPage() {
  const { user, canEdit } = useAuth();
  const canEditCustomer = user?.role === 'inspector' || user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [inspectionType, setInspectionType] = useState(searchParams.get('type') || 'all');
  const [recordType, setRecordType] = useState(searchParams.get('record_type') || 'all');
  const [createdFrom, setCreatedFrom] = useState(searchParams.get('created_from') || '');
  const [createdTo, setCreatedTo] = useState(searchParams.get('created_to') || '');
  
  // Detail drawer state
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cylinders, setCylinders] = useState([]);
  const [attachments, setAttachments] = useState([]);

  // Customer-details edit state (name & phone only)
  const [customerEdit, setCustomerEdit] = useState({ name: '', phone: '' });
  const [customerOriginal, setCustomerOriginal] = useState({ name: '', phone: '' });
  const [customerSaving, setCustomerSaving] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    loadInspections();
  }, [status, inspectionType, recordType, createdFrom, createdTo, page]);

  const loadInspections = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: page * pageSize
      };
      if (search) params.search = search;
      if (status && status !== 'all') params.status = status;
      if (inspectionType && inspectionType !== 'all') params.inspection_type = inspectionType;
      if (recordType && recordType !== 'all') params.record_type = recordType;
      if (createdFrom) params.start_date = createdFrom;
      if (createdTo) {
        // Include the whole end-day in the filter
        params.end_date = `${createdTo}T23:59:59`;
      }
      
      const response = await inspectionsAPI.getAll(params);
      // Handle both old (array) and new (paginated object) response formats
      const data = response.data;
      if (Array.isArray(data)) {
        setInspections(data);
        setTotal(data.length);
      } else {
        setInspections(data.inspections || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0); // Reset to first page on search
    loadInspections();
  };

  const handleViewDetails = async (inspection) => {
    setSelectedInspection(inspection);
    setDrawerOpen(true);
    setDetailLoading(true);

    try {
      // Lazy load full inspection details
      const fullInspRes = await inspectionsAPI.getById(inspection.id);
      setSelectedInspection(fullInspRes.data);

      // Seed customer-edit fields from the record data on the inspection
      const nm = fullInspRes.data?.owner_name || '';
      const ph = fullInspRes.data?.owner_phone || '';
      setCustomerEdit({ name: nm, phone: ph });
      setCustomerOriginal({ name: nm, phone: ph });

      const [cylRes, attRes] = await Promise.all([
        inspectionsAPI.getCylinders(inspection.id),
        inspectionsAPI.getAttachments(inspection.id),
      ]);
      setCylinders(cylRes.data);
      setAttachments(attRes.data);
    } catch (err) {
      console.error('Failed to load details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Reset unsaved customer edits when the drawer closes
  useEffect(() => {
    if (!drawerOpen) {
      setCustomerEdit({ name: '', phone: '' });
      setCustomerOriginal({ name: '', phone: '' });
    }
  }, [drawerOpen]);

  const customerDirty =
    customerEdit.name !== customerOriginal.name ||
    customerEdit.phone !== customerOriginal.phone;

  const handleSaveCustomer = async () => {
    if (!selectedInspection?.id) return;
    const name = (customerEdit.name || '').trim();
    const phone = (customerEdit.phone || '').replace(/\D/g, '');
    if (!name) {
      toast.error('Customer Name is required');
      return;
    }
    if (!phone || phone.length < 9) {
      toast.error('Customer Phone Number is required (min 9 digits)');
      return;
    }
    setCustomerSaving(true);
    try {
      const res = await inspectionsAPI.updateCustomer(selectedInspection.id, {
        owner_name: name,
        owner_phone: phone,
      });
      toast.success(res.data?.message || 'Customer details updated successfully');
      setCustomerOriginal({ name: res.data?.owner_name || name, phone: res.data?.owner_phone || phone });
      setCustomerEdit({ name: res.data?.owner_name || name, phone: res.data?.owner_phone || phone });
      setSelectedInspection((prev) =>
        prev ? { ...prev, owner_name: res.data?.owner_name || name, owner_phone: res.data?.owner_phone || phone } : prev
      );
      // Refresh the list so the parent table also reflects the change if columns use it
      loadInspections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update customer details');
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleDownloadCertificate = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      downloadBlob(response.data, `certificate_${inspectionId}.pdf`);
      toast.success('Certificate downloaded');
    } catch (err) {
      toast.error('Failed to download certificate');
    }
  };

  const handlePrintCertificate = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success('Opening certificate for printing');
    } catch (err) {
      toast.error('Failed to load certificate for printing');
    }
  };

  const handleDownloadQR = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      downloadBlob(response.data, `qr_sticker_${inspectionId}.pdf`);
      toast.success('QR sticker PDF downloaded');
    } catch (err) {
      toast.error('Failed to download QR sticker');
    }
  };

  const handleDownloadQRImage = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRImage(inspectionId);
      downloadBlob(response.data, `qr_code_${inspectionId}.png`);
      toast.success('QR code image downloaded');
    } catch (err) {
      toast.error('Failed to download QR image');
    }
  };

  const handlePrintQR = async (inspectionId) => {
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success('Opening QR sticker for printing');
    } catch (err) {
      toast.error('Failed to load QR sticker for printing');
    }
  };

  const getIdentifier = (inspection) => {
    return inspection.registration_number || inspection.storage_serial_number || 'N/A';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inspections-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Inspections</h1>
          <p className="text-muted-foreground mt-1">
            View and manage all inspection records
          </p>
        </div>
        {canEdit && (
          <Button asChild data-testid="new-inspection-btn">
            <Link to="/inspections/new">
              <Plus className="mr-2 h-4 w-4" />
              New Inspection
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reg #, serial, verification #..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full md:w-32" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>

            <Select value={inspectionType} onValueChange={setInspectionType}>
              <SelectTrigger className="w-full md:w-36" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="periodic">Periodic</SelectItem>
                <SelectItem value="after_accident">After Accident</SelectItem>
                <SelectItem value="after_repair">After Repair</SelectItem>
              </SelectContent>
            </Select>

            <Select value={recordType} onValueChange={setRecordType}>
              <SelectTrigger className="w-full md:w-36" data-testid="record-type-filter">
                <SelectValue placeholder="Record Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Records</SelectItem>
                <SelectItem value="car">🚗 Light Vehicle</SelectItem>
                <SelectItem value="3w">🛺 3-Wheeler</SelectItem>
                <SelectItem value="bus">🚌 Bus</SelectItem>
                <SelectItem value="truck">🛻 Truck</SelectItem>
                <SelectItem value="trailer">🚛 Trailer</SelectItem>
                <SelectItem value="cng_storage">🏭 CNG Storage</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                className="w-full md:w-40"
                value={createdFrom}
                onChange={(e) => { setCreatedFrom(e.target.value); setPage(0); }}
                title="Created from"
                data-testid="created-from-filter"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                className="w-full md:w-40"
                value={createdTo}
                onChange={(e) => { setCreatedTo(e.target.value); setPage(0); }}
                title="Created to"
                data-testid="created-to-filter"
              />
              {(createdFrom || createdTo) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCreatedFrom(''); setCreatedTo(''); setPage(0); }}
                  data-testid="clear-date-filter"
                >
                  Clear
                </Button>
              )}
            </div>

            <Button type="submit" data-testid="search-btn">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Inspections Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : inspections.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-medium text-lg mb-1">No inspections found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {search || status !== 'all' || inspectionType !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first inspection'}
              </p>
              {canEdit && (
                <Button asChild>
                  <Link to="/inspections/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Inspection
                  </Link>
                </Button>
              )}
            </div>
          ) : (
              <div className="overflow-x-auto">
                <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-[140px]">Verification #</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Inspector</TableHead>
                    <TableHead className="hidden lg:table-cell">Valid Until</TableHead>
                    <TableHead className="hidden lg:table-cell">Region</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspections.map((inspection) => (
                    <TableRow 
                      key={inspection.id} 
                      className="table-row-hover cursor-pointer"
                      onClick={() => handleViewDetails(inspection)}
                      data-testid={`inspection-row-${inspection.id}`}
                    >
                      <TableCell>
                        <span className="font-mono text-xs">
                          {inspection.verification_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-mono font-medium">
                            {getIdentifier(inspection)}
                          </span>
                          {inspection.record_type && (
                            <p className="text-xs text-muted-foreground">
                              {getRecordTypeLabel(inspection.record_type)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm capitalize">
                          {getInspectionTypeLabel(inspection.inspection_type)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(inspection.status)}
                          <Badge 
                            variant="outline"
                            className={cn("capitalize", getStatusColor(inspection.status))}
                          >
                            {inspection.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {inspection.inspector_name || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {inspection.valid_to ? formatDate(inspection.valid_to) : '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {inspection.region || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(inspection)}
                            title="View Details"
                            data-testid={`view-${inspection.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {inspection.status === 'draft' && (
                            <Button
                              asChild
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1"
                              title="Edit / Continue Draft"
                              data-testid={`edit-draft-${inspection.id}`}
                            >
                              <Link to={`/inspections/${inspection.id}/edit`}>
                                <ClipboardCheck className="h-3.5 w-3.5" />
                                <span className="text-xs">Edit / Continue</span>
                              </Link>
                            </Button>
                          )}
                          {inspection.has_certificate && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadCertificate(inspection.id)}
                                title="Download Certificate"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePrintCertificate(inspection.id)}
                                title="Print Certificate"
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadQR(inspection.id)}
                                title="Download QR"
                              >
                                <QrCode className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
            
            {/* Pagination Controls - Always visible when not loading */}
            {!loading && inspections.length > 0 && (
              <div className="flex items-center justify-between py-4 px-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total} inspections
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {page + 1} of {Math.ceil(total / pageSize) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * pageSize >= total || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Inspection Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedInspection && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="flex items-center gap-2">
                  <div className={cn(
                    "h-3 w-3 rounded-full",
                    selectedInspection.status === 'valid' ? 'bg-emerald-500' :
                    selectedInspection.status === 'expired' ? 'bg-red-500' :
                    selectedInspection.status === 'failed' ? 'bg-red-500' :
                    'bg-amber-500'
                  )} />
                  {selectedInspection.verification_number}
                </SheetTitle>
                <SheetDescription>
                  {getIdentifier(selectedInspection)} • {getRecordTypeLabel(selectedInspection.record_type)}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full grid grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="cylinders">Cylinders</TabsTrigger>
                  <TabsTrigger value="tests">Tests</TabsTrigger>
                  <TabsTrigger value="attachments">Files</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <Badge 
                        variant="outline"
                        className={cn("mt-1 capitalize", getStatusColor(selectedInspection.status))}
                      >
                        {selectedInspection.status}
                      </Badge>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Type</p>
                      <p className="text-sm font-medium mt-1 capitalize">
                        {getInspectionTypeLabel(selectedInspection.inspection_type)}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Valid From</p>
                      <p className="text-sm font-medium mt-1">
                        {selectedInspection.valid_from ? formatDate(selectedInspection.valid_from) : '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Valid Until</p>
                      <p className="text-sm font-medium mt-1">
                        {selectedInspection.valid_to ? formatDate(selectedInspection.valid_to) : '-'}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Inspector</p>
                        <p className="text-sm font-medium">{selectedInspection.inspector_name || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Region</p>
                        <p className="text-sm font-medium">{selectedInspection.region || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Next Inspection</p>
                        <p className="text-sm font-medium">
                          {selectedInspection.next_inspection_date 
                            ? formatDate(selectedInspection.next_inspection_date) 
                            : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Customer Details — editable (Name & Phone only) */}
                  <div className="rounded-lg border p-3 space-y-3" data-testid="customer-details-section">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <User className="h-4 w-4" /> Customer Details
                      </p>
                      {!canEditCustomer && (
                        <Badge variant="outline" className="text-[10px]">Read only</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Customer Name</label>
                        <Input
                          value={customerEdit.name}
                          disabled={!canEditCustomer || customerSaving}
                          onChange={(e) => setCustomerEdit((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Full name"
                          data-testid="customer-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Customer Phone Number</label>
                        <Input
                          value={customerEdit.phone}
                          disabled={!canEditCustomer || customerSaving}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9+ \-()]/g, '');
                            setCustomerEdit((p) => ({ ...p, phone: v }));
                          }}
                          placeholder="e.g. 0712345678"
                          data-testid="customer-phone-input"
                        />
                      </div>
                    </div>
                    {canEditCustomer && (
                      <div className="flex items-center justify-end gap-2 pt-1">
                        {customerDirty && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCustomerEdit({ ...customerOriginal })}
                            disabled={customerSaving}
                            data-testid="customer-cancel-btn"
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={handleSaveCustomer}
                          disabled={!customerDirty || customerSaving}
                          data-testid="customer-save-btn"
                        >
                          {customerSaving ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving…</>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Only Customer Name and Phone Number can be edited here. Other inspection, vehicle and certificate information remains unchanged.
                    </p>
                  </div>

                  {selectedInspection.notes && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Notes</p>
                        <p className="text-sm">{selectedInspection.notes}</p>
                      </div>
                    </>
                  )}

                  {selectedInspection.has_certificate && (
                    <>
                      <Separator />
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleDownloadCertificate(selectedInspection.id)}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          Download
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handlePrintCertificate(selectedInspection.id)}
                        >
                          <Printer className="mr-1 h-3 w-3" />
                          Print
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleDownloadQR(selectedInspection.id)}
                          title="Download as PDF"
                        >
                          <FileText className="mr-1 h-3 w-3" />
                          QR PDF
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handleDownloadQRImage(selectedInspection.id)}
                          title="Download as Image"
                        >
                          <Image className="mr-1 h-3 w-3" />
                          QR Image
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => handlePrintQR(selectedInspection.id)}
                          title="Print QR Sticker"
                        >
                          <Printer className="mr-1 h-3 w-3" />
                          Print
                        </Button>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="cylinders" className="mt-4">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : cylinders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Cylinder className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No cylinders recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cylinders.map((cyl, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono font-medium text-sm">
                              {cyl.serial_number}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              #{idx + 1}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Manufacturer:</span>
                              <span className="ml-1">{cyl.manufacturer || '-'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Capacity:</span>
                              <span className="ml-1">{cyl.capacity ? `${cyl.capacity}L` : '-'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Mfg Date:</span>
                              <span className="ml-1">{cyl.manufacturing_date || '-'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Expiry:</span>
                              <span className="ml-1">{cyl.expiry_date || '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="tests" className="mt-4">
                  <div className="space-y-3">
                    {[
                      { key: 'visual_inspection_passed', label: 'Visual Inspection', desc: 'Physical damage, corrosion check' },
                      { key: 'gas_leak_test_passed', label: 'Gas Leak Test', desc: 'Connections and valves', fallback: 'leak_test_passed' },
                      { key: 'pressure_test_passed', label: 'Pressure Test', desc: 'Pressure holding capacity' },
                      { key: 'ndt_test_passed', label: 'NDT Test', desc: 'Non-destructive testing' },
                    ].map((test) => {
                      const raw = selectedInspection[test.key];
                      const value = raw === null || raw === undefined ? (test.fallback ? selectedInspection[test.fallback] : null) : raw;
                      const isPass = value === true;
                      const isFail = value === false;
                      const notConducted = value === null || value === undefined;
                      return (
                        <div
                          key={test.key}
                          className={cn(
                            "p-3 rounded-lg border flex items-center justify-between",
                            isPass && "bg-emerald-50 border-emerald-200",
                            isFail && "bg-red-50 border-red-200",
                            notConducted && "bg-gray-50 border-gray-200"
                          )}
                        >
                          <div>
                            <p className="font-medium text-sm">{test.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {test.desc} · {isPass ? 'Passed' : isFail ? 'Failed' : 'Not Conducted'}
                            </p>
                          </div>
                          {isPass ? (
                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                          ) : isFail ? (
                            <XCircle className="h-5 w-5 text-red-500" />
                          ) : (
                            <MinusCircle className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="attachments" className="mt-4">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : attachments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No attachments</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{att.name}</p>
                              <p className="text-xs text-muted-foreground">{att.file_type}</p>
                            </div>
                          </div>
                          {att.file_url && (
                            <Button variant="ghost" size="icon" asChild>
                              <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <Separator className="my-4" />

              <div className="text-xs text-muted-foreground space-y-1">
                <p>Created: {formatDateTime(selectedInspection.created_at)}</p>
                <p>ID: {selectedInspection.id}</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
